import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface HeroProps {
  isInitialized: boolean;
}

const Hero: React.FC<HeroProps> = ({ isInitialized }) => {
  const { t } = useTranslation();

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background - Optimized image loading */}
      <div className="absolute inset-0 bg-black">
        <picture>
          <source
            srcSet="/BEN-800.webp 800w, /BEN-1200.webp 1200w, /BEN-optimized.webp"
            type="image/webp"
          />
          <img
            src="/BEN-optimized.webp"
            alt="La Frencherie background"
            loading="eager"
            decoding="async"
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-contain opacity-30 py-8 sm:py-12 lg:py-16 will-change-transform motion-reduce:transform-none"
            style={{ transform: 'translateZ(0)' }}
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black opacity-80"></div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16 sm:py-20 lg:py-24">
        
        {/* Service Tags - Refined positioning */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInitialized ? { opacity: 1, y: 0 } : {}}
          transition={{
            duration: 1.5,
            delay: 0.6,
            ease: 'easeOut',
          }}
          className="mb-8 sm:mb-12 lg:mb-16"
        >
          <h1 className="text-xs sm:text-sm md:text-base lg:text-lg font-light tracking-[0.2em] sm:tracking-[0.3em] text-gray-400 font-serif uppercase">
            {t('hero.tagline')}
          </h1>
        </motion.div>

        {/* Brand Name - Enhanced prominence */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={isInitialized ? { opacity: 1, scale: 1 } : {}}
          transition={{
            duration: 2,
            delay: 0.2,
            ease: 'easeOut',
          }}
          className="mb-8 sm:mb-12 lg:mb-16"
        >
          <div className="text-3xl xs:text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-light tracking-[0.1em] sm:tracking-[0.15em] text-white mb-6 sm:mb-8 font-serif">
            {t('hero.title')}
          </div>
          <div className="w-32 h-px bg-gradient-to-r from-transparent via-white to-transparent mx-auto opacity-40"></div>
        </motion.div>

        {/* Refined Tagline */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInitialized ? { opacity: 1, y: 0 } : {}}
          transition={{
            duration: 1.5,
            delay: 1.0,
            ease: 'easeOut',
          }}
          className="mb-12 sm:mb-16 lg:mb-20"
        >
          <p className="text-sm xs:text-base sm:text-lg md:text-xl lg:text-2xl font-light tracking-wide text-gray-400 max-w-4xl mx-auto leading-relaxed font-serif">
            {t('hero.subtitle')}
          </p>
        </motion.div>

        {/* Enhanced CTA Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInitialized ? { opacity: 1, y: 0 } : {}}
          transition={{
            duration: 1.5,
            delay: 1.4,
            ease: 'easeOut',
          }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-8 lg:gap-12 justify-center items-center">
            <motion.a
              href="#services"
              className="group relative px-6 sm:px-8 py-4 border border-gray-600 bg-transparent text-white text-xs sm:text-sm tracking-[0.2em] font-light uppercase transition-all duration-300 hover:border-gray-400 hover:bg-white hover:bg-opacity-5 overflow-hidden"
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <span className="relative z-10">{t('hero.buttons.creative')}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
            </motion.a>
            
            <motion.a
              href="#lifestyle-concierge"
              className="group relative px-6 sm:px-8 py-4 border border-gray-600 bg-transparent text-white text-xs sm:text-sm tracking-[0.2em] font-light uppercase transition-all duration-300 hover:border-gray-400 hover:bg-white hover:bg-opacity-5 overflow-hidden"
              whileHover={{ y: -2 }}
              transition={{ duration: 0.2 }}
            >
              <span className="relative z-10">{t('hero.buttons.concierge')}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
            </motion.a>
          </div>
        </motion.div>

        {/* Subtle brand footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={isInitialized ? { opacity: 1 } : {}}
          transition={{
            duration: 1.5,
            delay: 1.8,
            ease: 'easeOut',
          }}
          className="text-xs tracking-[0.3em] text-gray-600 font-light uppercase"
        >
          {t('hero.footer')}
        </motion.div>
      </div>

      {/* Enhanced Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
        initial={{ opacity: 0 }}
        animate={isInitialized ? { opacity: 1 } : {}}
        transition={{ delay: 2.0, duration: 1.0 }}
      >
        <motion.div 
          className="w-px h-12 bg-gradient-to-b from-transparent via-white to-transparent opacity-40"
          animate={{ 
            opacity: [0.4, 0.8, 0.4],
            scaleY: [1, 1.2, 1]
          }}
          transition={{ 
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        ></motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;